import {
  type ExecutedDrop,
  getMappingDisplay,
} from '@/api/hooks/useOrderDetails'
import type { components } from '@/types/api/v1'
import { formatCompactDateTime } from '@/utils/dateFormatters'
import { formatVolume } from '@/utils/stringFormatters'
import { EnvironmentOutlined } from '@ant-design/icons'
import { GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

type VolumeUnits = components['schemas']['VolumeUnits']

interface SiteDropsGridProps {
  executedDrops: ExecutedDrop[]
  units?: VolumeUnits | null
}

interface FlattenedDropRow {
  id: string
  site: string
  tank: string
  product: string
  pre_drop_volume: number | null
  post_drop_volume: number | null
  quantity: number
  time: string
}

export function SiteDropsGrid({ executedDrops, units }: SiteDropsGridProps) {
  // Flatten executed drops - one row per detail
  const rowData = useMemo<FlattenedDropRow[]>(() => {
    const rows: FlattenedDropRow[] = []

    for (const [dropIndex, drop] of executedDrops.entries()) {
      const site = getMappingDisplay(drop.site)

      for (const [detailIndex, detail] of drop.details.entries()) {
        rows.push({
          id: `${dropIndex}-${detailIndex}`,
          site,
          tank: detail.tank ? getMappingDisplay(detail.tank) : '-',
          product: getMappingDisplay(detail.product),
          pre_drop_volume: detail.pre_drop_volume ?? null,
          post_drop_volume: detail.post_drop_volume ?? null,
          quantity: detail.quantity,
          time: formatCompactDateTime(detail.post_drop_time),
        })
      }
    }

    return rows
  }, [executedDrops])

  const totalDelivered = useMemo(
    () => rowData.reduce((sum, row) => sum + row.quantity, 0),
    [rowData],
  )

  const columnDefs = useMemo<ColDef<FlattenedDropRow>[]>(
    () => [
      {
        headerName: 'Site',
        field: 'site',
        minWidth: 150,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Tank',
        field: 'tank',
        minWidth: 100,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Product',
        field: 'product',
        minWidth: 120,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Pre-Drop',
        field: 'pre_drop_volume',
        minWidth: 110,
        sortable: true,
        valueFormatter: (params) => formatVolume(params.value, units),
      },
      {
        headerName: 'Post-Drop',
        field: 'post_drop_volume',
        minWidth: 110,
        sortable: true,
        valueFormatter: (params) => formatVolume(params.value, units),
      },
      {
        headerName: 'Delivered',
        field: 'quantity',
        minWidth: 110,
        sortable: true,
        valueFormatter: (params) => formatVolume(params.value, units),
      },
      {
        headerName: 'Time',
        field: 'time',
        minWidth: 180,
        sortable: true,
      },
    ],
    [units],
  )

  const isEmpty = rowData.length === 0

  return (
    <Vertical>
      {/* Section header */}
      <Horizontal
        alignItems='center'
        justifyContent='space-between'
        style={{ padding: '12px 0px' }}
      >
        <Horizontal gap={6} alignItems='center'>
          <EnvironmentOutlined
            style={{ fontSize: 14, color: 'var(--gray-600)' }}
          />
          <Texto
            style={{ fontSize: 15, fontWeight: 600, color: 'var(--gray-600)' }}
          >
            Site Drops
          </Texto>
        </Horizontal>
        {!isEmpty && (
          <Horizontal gap={8} alignItems='center'>
            <Texto style={{ fontSize: 13 }}>Total Delivered:</Texto>
            <Texto style={{ fontSize: 14, fontWeight: 600 }}>
              {formatVolume(totalDelivered, units)}
            </Texto>
          </Horizontal>
        )}
      </Horizontal>

      {/* Grid */}
      <div style={{ height: 320 }}>
        <GraviGrid
          rowData={rowData}
          columnDefs={columnDefs}
          sideBar={false}
          agPropOverrides={{
            getRowId: (params) => params.data.id,
            suppressRowClickSelection: true,
            rowGroupPanelShow: 'never',
            defaultColDef: {
              sortable: true,
              resizable: true,
            },
            overlayNoRowsTemplate:
              '<span style="color: var(--muted-foreground); font-size: 13px;">No drops recorded</span>',
          }}
        />
      </div>
    </Vertical>
  )
}
